/**
 * Store Status Helper
 * Centralized business logic for calculating store status
 * This is the single source of truth for store status calculation logic
 * Used by both store modules and components
 */

import { isRestaurantClosed } from "@/composables/useRestaurantHours";
import moment from "moment-timezone";

/**
 * Calculates store status for any store location
 * @param {Object} store - Store location object
 * @param {Array} allDiningOptions - All available dining options
 * @param {string} timezone - Store timezone
 * @returns {string} 'open' | 'closed' | 'schedule_only'
 */
export function calculateStoreStatus(
  store,
  allDiningOptions = [],
  timezone = "UTC"
) {
  if (!store) {
    return "open"; // Can't determine, assume open
  }

  // If no dining options, check store basic hours
  if (allDiningOptions.length === 0) {
    if (store.start_time && store.close_time) {
      const isClosed = isRestaurantClosed(null, timezone, store);
      return isClosed ? "closed" : "open";
    }
    return "open"; // Can't determine, assume open
  }

  const now = moment().tz(timezone);
  const todayDay = now.isoWeekday(); // Returns 1-7 (Monday=1, Sunday=7)

  // Check each dining option for today's timings
  let hasOpenOption = false;
  let hasScheduleOption = false;
  let hasAnyTimingForToday = false;
  let allTimingsClosed = true;

  for (const option of allDiningOptions) {
    // Check if option supports scheduling (regardless of timings)
    if (option.is_scheduling_enabled === true) {
      hasScheduleOption = true;
    }

    if (!option.timings || option.timings.length === 0) {
      continue;
    }

    // Find all timings for today (both closed and open)
    const allTimingsForToday = option.timings.filter(
      (timing) => timing.day_of_week === todayDay
    );

    if (allTimingsForToday.length === 0) {
      continue;
    }

    hasAnyTimingForToday = true;

    // Check if all timings for today have is_closed: true
    const allClosedForThisOption = allTimingsForToday.every(
      (timing) => timing.is_closed === true
    );

    if (allClosedForThisOption) {
      continue;
    } else {
      allTimingsClosed = false;
    }

    // Find timings for today with is_closed: false
    const timingsForToday = allTimingsForToday.filter(
      (timing) => timing.is_closed === false
    );

    if (timingsForToday.length === 0) {
      continue;
    }

    // Check if current time is within any timing's hours for today
    const isOpen = timingsForToday.some((timing) => {
      const today = now.clone().startOf("day");
      const openTime = today.clone().add(moment.duration(timing.opening_time));
      const closeTime = today.clone().add(moment.duration(timing.closing_time));

      // Handle case where closing time is next day (e.g., 23:00 - 02:00)
      if (closeTime.isBefore(openTime) || closeTime.isSame(openTime)) {
        return now.isSameOrAfter(openTime) || now.isBefore(closeTime);
      }

      // Normal case: opening time < closing time
      // For closing_time like "23:59:00", add 1 minute to include the exact closing time
      const nextMinuteAfterClose = closeTime.clone().add(1, "minute");
      const isWithinHours =
        now.isSameOrAfter(openTime) && now.isBefore(nextMinuteAfterClose);
      return isWithinHours;
    });

    if (isOpen) {
      hasOpenOption = true;
    }
  }

  // Store is open: when current time is within at least one timing
  if (hasOpenOption) {
    return "open";
  }

  // If all timings for today have is_closed: true
  if (hasAnyTimingForToday && allTimingsClosed) {
    // If at least one option has is_scheduling_enabled === true, it's schedule_only
    if (hasScheduleOption) {
      return "schedule_only";
    }
    return "closed";
  }

  // Store is schedule_only: when current time is NOT within hours
  // but at least one option has is_scheduling_enabled === true
  if (hasScheduleOption) {
    return "schedule_only";
  }

  // Store is closed: when current time is NOT within hours
  // and no options have is_scheduling_enabled
  return "closed";
}
